/**
 * Standalone brand assets — string builders for the committed SVG files in
 * `assets/brand/`. The files are GENERATED from these builders (which share
 * `geometry.ts` with the element) so the letterform can never drift between
 * component and asset; a parity test asserts the committed files match.
 *
 * Regenerate after touching geometry:
 *
 *   bun -e 'import { writeBrandAssets } from "./src/brand/assets";
 *           await writeBrandAssets();'   # from packages/web-kit
 *
 * PNG favicon sizes are NOT generated here (no rasterizer dependency).
 * When needed, render `favicon.svg` at 16/32/48/180/192/512 with any
 * rasterizer, e.g.:  `rsvg-convert -w 32 -h 32 favicon.svg > favicon-32.png`
 * (or resvg/sharp). The 16px case is the design constraint the favicon
 * geometry is built for — verify that size first.
 */

import { STEM_PATH, BOWL_PATH, DOT, VIEW, dotCx } from "./geometry";

/** Brand inks (mirrors the CSS defaults in `styles.ts`). */
export const BRAND_INK_CREAM = "#f2ede3";
export const BRAND_INK_CHARCOAL = "#211d18";
export const BRAND_DOT_EMBER = "#de8d51";

export interface LogoSvgOptions {
  /** Stroke/fill ink. Default cream (dark theme first). */
  ink?: string;
  /** Dot color. Default the ink (spaceless mark). */
  dot?: string;
}

/** The bare »P.« mark (no app name) as a standalone SVG document. */
export function logoSvg(options: LogoSvgOptions = {}): string {
  const ink = options.ink ?? BRAND_INK_CREAM;
  const dot = options.dot ?? ink;
  const width = VIEW.minWidth;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${VIEW.height}" role="img" aria-label="P.">
  <path id="p-stem" fill="${ink}" d="${STEM_PATH}"/>
  <path id="p-bowl" fill="${ink}" d="${BOWL_PATH}"/>
  <circle id="p-dot" fill="${dot}" cx="${dotCx(0)}" cy="${DOT.cy}" r="${DOT.r}"/>
</svg>
`;
}

/**
 * The favicon — the ratified tile variant (concept sheet 1, bottom-right):
 * the DARK two-stroke stencil p on the CREAM rounded-square tile — the
 * inverse of the on-dark mark, with NO dot. Pixel-measured from the
 * reference tile:
 *
 *   - stem: the ACTUAL v2 `STEM_PATH`, placed by transform; the favicon cut
 *     carries ~12 % extra horizontal weight (sx > sy). Glyph height ≈ 54 %
 *     of the tile.
 *   - bowl: a favicon-specific cut of the sail (`FAVICON_BOWL`, below) —
 *     the app-name gap collapses to a tight stencil slit (≈ 2.7 stem
 *     widths), the top hook is subtler, and the bottom tail sweeps further
 *     LEFT-down than the display bowl.
 *   - tile corner radius ≈ 10.4/64.
 *
 * The slit and the bowl's chord are the counters that must survive 16 px.
 */

/**
 * The favicon bowl — same anatomy as `BOWL_PATH` (solid sail: outer arc,
 * a vertical chord, tips hooking LEFT), re-cut for the tile as a
 * near-symmetric comma-sail: both tips reach x ≈ 35.7, stay ~2.5 units
 * thick to the end and cut BLUNT (no hairline points — they must survive
 * 16 px). Row-profile-measured off the reference: chord x 38.7, belly
 * x 44.7 at y 26.5, tips at y 16 / 36.6.
 */
const FAVICON_BOWL = [
  "M 35.6 15.9", // top tip, left corner — hooks up-LEFT like the display bowl
  "C 36.6 15.7 37.5 15.8 38.1 16.1", // blunt top cut (softly rounded)
  "C 41 17.4 43.3 19.9 44.1 23", // outer edge: shoulder out and down
  "C 44.85 26 44.6 28.5 44.15 30.8", // around the belly (max ≈ 44.7 at y 26.5)
  "C 43.8 31.6 43.4 32.4 42.8 33.2", // easing into the tail
  "C 41.5 34.6 40 35.8 38.3 36.6", // the tail sweeps down-LEFT, still thick
  "C 37.4 37 36.5 36.9 35.8 36.6", // blunt bottom cut
  "C 36.9 36.1 38.1 35.2 38.5 34.4", // inner edge rises back right
  "C 38.65 34 38.7 33.8 38.7 33.4", // …meeting the chord bottom
  "L 38.7 20.2", // the chord — straight, vertical
  "C 38.6 18.5 37.2 17 35.6 15.9", // chord top curls out to the tip
  "Z",
].join(" ");

export function faviconSvg(): string {
  // Stem: measured body x 21.5..26.3, y 15.7..50.5.
  const stem = { sx: 0.278, sy: 0.2486, tx: 13.2, ty: 11.72 };
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="P">
  <rect id="tile" width="64" height="64" rx="10.4" fill="${BRAND_INK_CREAM}"/>
  <path id="p-stem" fill="${BRAND_INK_CHARCOAL}" transform="translate(${stem.tx} ${stem.ty}) scale(${stem.sx} ${stem.sy})" d="${STEM_PATH}"/>
  <path id="p-bowl" fill="${BRAND_INK_CHARCOAL}" d="${FAVICON_BOWL}"/>
</svg>
`;
}

/** Write the committed asset files (run from `packages/web-kit`). */
export async function writeBrandAssets(
  dir = `${import.meta.dir}/../../assets/brand`
): Promise<void> {
  await Bun.write(`${dir}/logo.svg`, logoSvg());
  await Bun.write(`${dir}/favicon.svg`, faviconSvg());
}
